<!-- AppTrow.vue -->
<template>
  <tr
    :class="{ 'as-clickable': clickable || !!to || !!href }"
    @click="handleClick"
  >
    <td
      v-for="(cell, i) in cells"
      :key="i"
      :class="{
        'as-right':  cell.align === 'right',
        'as-center': cell.align === 'center',
      }"
    >
      <!-- Avatar -->
      <template v-if="cell.type === 'avatar'">
        <div class="as-avatar-cell">
          <div class="as-avatar" :style="avatarStyle(cell.value)">{{ iniciais(cell.value) }}</div>
          <div>
            <div class="as-name">{{ cell.value }}</div>
            <div v-if="cell.sub" class="as-sub">{{ cell.sub }}</div>
          </div>
        </div>
      </template>

      <!-- Badge -->
      <template v-else-if="cell.type === 'badge'">
        <span class="as-badge" :style="{ background: cell.bg, color: cell.text }">
          <span class="as-dot" :style="{ background: cell.dot }"></span>
          {{ cell.label ?? cell.value }}
        </span>
      </template>

      <!-- Moeda -->
      <template v-else-if="cell.type === 'currency'">
        <span class="as-currency">{{ fmt.currency(cell.value) }}</span>
      </template>

      <!-- Data -->
      <template v-else-if="cell.type === 'date'">
        <span class="as-date">{{ fmt.date(cell.value) }}</span>
        <span v-if="cell.relative" class="as-date-sub">{{ fmt.relative(cell.value) }}</span>
      </template>

      <!-- Boolean -->
      <template v-else-if="cell.type === 'boolean'">
        <span class="as-bool" :class="cell.value ? 'as-yes' : 'as-no'">
          {{ cell.value ? (cell.trueLabel ?? 'Sim') : (cell.falseLabel ?? 'Não') }}
        </span>
      </template>

      <!-- Texto (default) -->
      <template v-else>
        <span class="as-text">{{ cell.value ?? '—' }}</span>
      </template>
    </td>

    <!-- slot para ações (botões no fim da linha) -->
    <td v-if="$slots.actions" class="as-actions" @click.stop>
      <slot name="actions" />
    </td>
  </tr>
</template>

<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'

/**
 * AppTrow — linha da tabela.
 *
 * A prop :data aceita um array de valores ou de objetos de célula.
 * Cada item pode ser:
 *
 *   — valor simples (string, number, boolean):
 *       :data="[item.name, item.price, item.active]"
 *
 *   — objeto de célula tipada:
 *       :data="[
 *         { type: 'avatar',   value: item.name,   sub: item.city },
 *         { type: 'badge',    value: item.status, label: 'Ativo', bg: '#EAF3DE', text: '#3B6D11', dot: '#97C459' },
 *         { type: 'currency', value: item.price,  align: 'right' },
 *         { type: 'date',     value: item.date,   relative: true },
 *         { type: 'boolean',  value: item.active, trueLabel: 'Ativo', falseLabel: 'Inativo' },
 *       ]"
 *
 *   — mistura dos dois:
 *       :data="[item.name, { type: 'currency', value: item.price, align: 'right' }, item.date]"
 *
 * Navegação:
 *   :to="'/rental/' + item.id"   → router-link interno
 *   :href="item.url"             → link externo
 *   :clickable="true"            → emite @click para o pai tratar
 */
const props = defineProps({
  data:      { type: Array,   default: () => [] },
  to:        { type: String,  default: null },
  href:      { type: String,  default: null },
  clickable: { type: Boolean, default: false },
})

const emit = defineEmits(['click'])
const router = useRouter()

// Normaliza cada item do array :data em um objeto de célula uniforme
const cells = computed(() =>
  props.data.map(item => {
    if (item !== null && typeof item === 'object') return item  // já é objeto
    return { value: item }                                      // valor simples → type: text
  })
)

function handleClick() {
  if (props.to)        router.push(props.to)
  else if (props.href) window.open(props.href, '_blank', 'noopener')
  else if (props.clickable) emit('click')
}

// ── Formatadores ──
const fmt = {
  currency: v => Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
  date(iso) {
    if (!iso) return '—'
    const [y, m, d] = String(iso).split('-')
    return `${d}/${m}/${y}`
  },
  relative(iso) {
    if (!iso) return ''
    const diff = Math.floor((Date.now() - new Date(iso)) / 86400000)
    if (diff === 0) return 'hoje'
    if (diff === 1) return 'ontem'
    if (diff < 0)  return `em ${Math.abs(diff)} dias`
    return `há ${diff} dias`
  },
}

// ── Avatar ──
function iniciais(nome) {
  if (!nome) return '?'
  return String(nome).trim().split(' ').slice(0, 2).map(p => p[0].toUpperCase()).join('')
}
const PALETTE = [
  { bg: '#E6F1FB', color: '#185FA5' },
  { bg: '#E1F5EE', color: '#0F6E56' },
  { bg: '#EEEDFE', color: '#534AB7' },
  { bg: '#FAEEDA', color: '#854F0B' },
  { bg: '#FAECE7', color: '#993C1D' },
  { bg: '#EAF3DE', color: '#3B6D11' },
]
const _cache = {}
let _idx = 0
function avatarStyle(nome) {
  if (!_cache[nome]) { _cache[nome] = PALETTE[_idx % PALETTE.length]; _idx++ }
  return { background: _cache[nome].bg, color: _cache[nome].color }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600&family=JetBrains+Mono:wght@500&display=swap');

tr {
  border-bottom: 0.5px solid rgba(0,0,0,0.06);
  transition: background 0.12s;
  font-family: 'Sora', sans-serif;
}
tr:last-child { border-bottom: none; }
tr.as-clickable { cursor: pointer; }
tr.as-clickable:hover { background: #f9f9f7; }

td { padding: 13px 16px; vertical-align: middle; color: #1a1a1a; font-size: 13px; }
.as-right  { text-align: right; }
.as-center { text-align: center; }
.as-actions { text-align: right; white-space: nowrap; }

/* Avatar */
.as-avatar-cell { display: flex; align-items: center; gap: 10px; }
.as-avatar {
  width: 34px; height: 34px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700; flex-shrink: 0;
}
.as-name { font-weight: 600; font-size: 13px; color: #1a1a1a; }
.as-sub  { font-size: 11px; color: #9a9a9a; margin-top: 1px; }

/* Badge */
.as-badge {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 11px; font-weight: 600;
  padding: 4px 10px; border-radius: 20px; white-space: nowrap;
}
.as-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }

/* Moeda */
.as-currency { font-family: 'JetBrains Mono', monospace; font-size: 13px; font-weight: 500; }

/* Data */
.as-date     { font-size: 12px; color: #6b6b6b; display: block; white-space: nowrap; }
.as-date-sub { font-size: 11px; color: #b0b0b0; display: block; }

/* Boolean */
.as-bool { font-size: 11px; font-weight: 600; padding: 3px 10px; border-radius: 20px; }
.as-yes  { background: #EAF3DE; color: #3B6D11; }
.as-no   { background: #F1EFE8; color: #5F5E5A; }

/* Texto */
.as-text { font-size: 13px; color: #1a1a1a; }
</style>